import { createBipartite, type BipartiteHandle } from '@learnforge/demo-render';
import {
  defineExperiment,
  type CustomExperimentSpec,
  type PanelRow,
} from '@learnforge/demo-render/harness';
import type { SetsContext, SetsKey } from '../types';
import {
  isInjective,
  isSurjective,
  isBijective,
  image,
  compose,
  inverse,
  type FnMap,
} from '../sets';

// ── State ─────────────────────────────────────────────────────────────────────

export interface State {
  domain: string[];
  codomain: string[];
  map: [string, string][];
  second?: [string, string][];
  advanced: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_DOMAIN = ['a', 'b', 'c'];
const DEFAULT_CODOMAIN = ['1', '2', '3'];
const DEFAULT_MAP: [string, string][] = [
  ['a', '1'],
  ['b', '2'],
  ['c', '3'],
];

// ── Preset state factories ────────────────────────────────────────────────────

/**
 * Bijection / identity preset: |domain| = |codomain| = 3, a→1, b→2, c→3.
 * Injective ✓  Surjective ✓  Bijective ✓
 */
export function presetIdentity(advanced: boolean): State {
  return {
    domain: ['a', 'b', 'c'],
    codomain: ['1', '2', '3'],
    map: [
      ['a', '1'],
      ['b', '2'],
      ['c', '3'],
    ],
    advanced,
  };
}

/**
 * Injection (not surjective) preset: |domain| = 3 < |codomain| = 4.
 * All images distinct, one codomain element left unhit.
 * Injective ✓  Surjective ✗  Bijective ✗
 */
export function presetInjection(advanced: boolean): State {
  return {
    domain: ['a', 'b', 'c'],
    codomain: ['1', '2', '3', '4'],
    map: [
      ['a', '1'],
      ['b', '2'],
      ['c', '3'],
    ],
    advanced,
  };
}

/**
 * Surjection (not injective) preset: |domain| = 4 > |codomain| = 3.
 * Every codomain element is covered; two domain elements share one target.
 * Injective ✗  Surjective ✓  Bijective ✗
 */
export function presetSurjection(advanced: boolean): State {
  return {
    domain: ['a', 'b', 'c', 'd'],
    codomain: ['1', '2', '3'],
    map: [
      ['a', '1'],
      ['b', '2'],
      ['c', '3'],
      ['d', '1'],
    ],
    advanced,
  };
}

// ── SVG viewBox dimensions ────────────────────────────────────────────────────

const BIPARTITE_WIDTH = 360;
const BIPARTITE_HEIGHT = 340;

// ── Pure helpers ──────────────────────────────────────────────────────────────

/**
 * Enforce "exactly one output per input": sets the image of `input` to `target`,
 * replacing any existing arrow from that input. Returns a new map array.
 */
export function setImage(
  map: [string, string][],
  input: string,
  target: string,
): [string, string][] {
  // Remove existing arrow(s) from this input, then add the new one.
  const filtered = map.filter(([from]) => from !== input);
  return [...filtered, [input, target]];
}

/**
 * Build an FnMap (ReadonlyMap) from a map array.
 */
function toFnMap(map: [string, string][]): FnMap {
  return new Map(map);
}

/**
 * Compute which codomain elements are hit by multiple domain elements
 * (non-injective collisions). Returns a Set of codomain target ids.
 */
export function collisionTargets(map: [string, string][]): Set<string> {
  const seen = new Map<string, number>();
  for (const [, target] of map) {
    seen.set(target, (seen.get(target) ?? 0) + 1);
  }
  const collisions = new Set<string>();
  for (const [target, count] of seen) {
    if (count > 1) {
      collisions.add(target);
    }
  }
  return collisions;
}

/**
 * Compute which codomain elements are NOT in the image (unhit).
 */
export function unhitTargets(map: [string, string][], codomain: string[]): Set<string> {
  const fnMap = toFnMap(map);
  const img = new Set(image(fnMap, Array.from(fnMap.keys())));
  const unhit = new Set<string>();
  for (const y of codomain) {
    if (!img.has(y)) {
      unhit.add(y);
    }
  }
  return unhit;
}

// ── Spec ──────────────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<State, SetsKey, SetsContext> {
  return {
    labelKey: 'experiment.functions.label',
    demoId: 'sets',
    advancedKey: 'functions',

    initialState(ctx): State {
      return {
        domain: DEFAULT_DOMAIN.slice(),
        codomain: DEFAULT_CODOMAIN.slice(),
        map: DEFAULT_MAP.slice(),
        advanced: ctx.advanced,
      };
    },

    panel(_state, _ctx): PanelRow<SetsKey, State>[] {
      const rows: PanelRow<SetsKey, State>[] = [
        {
          kind: 'advanced-toggle',
          labelKey: 'btn.advanced',
        },
      ];
      return rows;
    },

    createRender(ctx, host, controls) {
      // ── Container ──
      const container = document.createElement('div');
      container.className = 'functions-container';
      host.appendChild(container);

      // ── Left column: bipartite diagram ──
      const diagramCol = document.createElement('div');
      diagramCol.className = 'functions-diagram-col';
      container.appendChild(diagramCol);

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'functions-svg');
      svg.setAttribute('viewBox', `0 0 ${BIPARTITE_WIDTH} ${BIPARTITE_HEIGHT}`);
      svg.setAttribute('width', String(BIPARTITE_WIDTH));
      svg.setAttribute('height', String(BIPARTITE_HEIGHT));
      diagramCol.appendChild(svg);

      // Column headers rendered inside SVG
      const headerLeft = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      headerLeft.setAttribute('class', 'functions-col-header');
      headerLeft.setAttribute('x', '80');
      headerLeft.setAttribute('y', '28');
      headerLeft.setAttribute('text-anchor', 'middle');
      svg.appendChild(headerLeft);

      const headerRight = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      headerRight.setAttribute('class', 'functions-col-header');
      headerRight.setAttribute('x', '280');
      headerRight.setAttribute('y', '28');
      headerRight.setAttribute('text-anchor', 'middle');
      svg.appendChild(headerRight);

      // ── Right column: readouts + badges ──
      const infoCol = document.createElement('div');
      infoCol.className = 'functions-info-col';
      container.appendChild(infoCol);

      // Stats readout
      const readout = document.createElement('div');
      readout.className = 'functions-readout';
      infoCol.appendChild(readout);

      // Image readout
      const imageReadout = document.createElement('div');
      imageReadout.className = 'functions-readout functions-readout--image';
      infoCol.appendChild(imageReadout);

      // Property badges (advanced only)
      const badgesEl = document.createElement('div');
      badgesEl.className = 'functions-badges';
      infoCol.appendChild(badgesEl);

      // Compose readout (advanced, shown when second present)
      const composeEl = document.createElement('div');
      composeEl.className = 'functions-compose';
      infoCol.appendChild(composeEl);

      // Inverse readout (advanced)
      const inverseEl = document.createElement('div');
      inverseEl.className = 'functions-inverse';
      infoCol.appendChild(inverseEl);

      // ── Handle ──
      let bipartiteHandle: BipartiteHandle | null = null;

      function update(state: State): void {
        const advanced = ctx.advanced;
        const { domain, codomain, map } = state;
        const fnMap = toFnMap(map);

        // Compute visual annotations
        const collisions = advanced ? collisionTargets(map) : new Set<string>();
        const unhit = advanced ? unhitTargets(map, codomain) : new Set<string>();

        // Build arrows with optional classNames
        const arrows = map.map(([from, to]) => {
          const toFull = `right:${to}`;
          if (advanced && collisions.has(to)) {
            return { from, to: toFull, className: 'is-collision' };
          }
          return { from, to: toFull };
        });

        // Build right nodes with optional classNames
        const rightNodes = codomain.map((id) => {
          if (advanced && unhit.has(id)) {
            return { id: `right:${id}`, label: id, className: 'is-unhit' };
          }
          return { id: `right:${id}`, label: id };
        });

        const leftNodes = domain.map((id) => ({ id, label: id }));

        headerLeft.textContent = ctx.i18n.t('label.domain');
        headerRight.textContent = ctx.i18n.t('label.codomain');

        if (bipartiteHandle === null) {
          bipartiteHandle = createBipartite(svg, {
            left: leftNodes,
            right: rightNodes,
            arrows,
            onToggleArrow(from, toFull) {
              // toFull is "right:<id>" — strip prefix
              const to = toFull.replace(/^right:/, '');
              controls.setState((prev) => ({
                ...prev,
                map: setImage(prev.map, from, to),
              }));
            },
          });
        } else {
          bipartiteHandle.update({ left: leftNodes, right: rightNodes, arrows });
        }

        // ── Readouts ──
        const img = image(fnMap, domain);
        readout.textContent = `${ctx.i18n.t('stat.domain-size')}: |A| = ${domain.length}`;
        imageReadout.textContent = `${ctx.i18n.t('stat.image-size')}: |f(A)| = ${img.length}`;

        // ── Badges (advanced only) ──
        badgesEl.innerHTML = '';
        composeEl.textContent = '';
        inverseEl.textContent = '';

        if (advanced) {
          const injective = isInjective(fnMap);
          const surjective = isSurjective(fnMap, codomain);
          const bijective = isBijective(fnMap, domain, codomain);

          const BADGES: { key: SetsKey; holds: boolean }[] = [
            { key: 'prop.injective', holds: injective },
            { key: 'prop.surjective', holds: surjective },
            { key: 'prop.bijective', holds: bijective },
          ];

          for (const { key, holds } of BADGES) {
            const badge = document.createElement('span');
            badge.className = holds
              ? 'functions-badge functions-badge--true'
              : 'functions-badge functions-badge--false';
            badge.textContent = ctx.i18n.t(key);
            badgesEl.appendChild(badge);
          }

          // Inverse indicator
          const inv = inverse(fnMap);
          const invExists = inv !== null;
          inverseEl.textContent = invExists
            ? `${ctx.i18n.t('label.inverse-exists')}: f⁻¹ ∃`
            : `${ctx.i18n.t('label.inverse-exists')}: ∅`;
          inverseEl.className = invExists
            ? 'functions-inverse functions-inverse--yes'
            : 'functions-inverse functions-inverse--no';

          // Compose: show g∘f when second map is present
          if (state.second !== undefined && state.second.length > 0) {
            const g = toFnMap(state.second);
            const gof = compose(g, fnMap);
            const composed = Array.from(gof.entries())
              .map(([x, y]) => `${x} ↦ ${y}`)
              .join(', ');
            composeEl.textContent = `${ctx.i18n.t('label.compose')} (g∘f): {${composed}}`;
            composeEl.className = 'functions-compose functions-compose--active';
          }
        }
      }

      return {
        update,
        dispose() {
          bipartiteHandle?.destroy();
          bipartiteHandle = null;
        },
      };
    },

    actions(state, ctx) {
      return {
        onReset() {
          return {
            domain: DEFAULT_DOMAIN.slice(),
            codomain: DEFAULT_CODOMAIN.slice(),
            map: DEFAULT_MAP.slice(),
            advanced: ctx.advanced,
          };
        },
        onRandomise() {
          const { domain, codomain } = state;
          // Each domain element maps to a random codomain element (always a function)
          const newMap: [string, string][] = domain.map((x) => {
            const idx = Math.floor(ctx.prng() * codomain.length);
            return [x, codomain[idx]!];
          });
          return { ...state, map: newMap };
        },
        presets: [
          { id: 'identity', labelKey: 'preset.fn-identity' as SetsKey },
          { id: 'injection', labelKey: 'preset.fn-injection' as SetsKey },
          { id: 'surjection', labelKey: 'preset.fn-surjection' as SetsKey },
        ],
        onPreset(_s: State, id: string) {
          switch (id) {
            case 'identity':
              // Bijection: |A| = |B| = 3, a→1, b→2, c→3. Inj ✓ Surj ✓ Bij ✓
              return presetIdentity(ctx.advanced);
            case 'injection':
              // Injection, not surjective: |A| = 3 < |B| = 4. Inj ✓ Surj ✗ Bij ✗
              return presetInjection(ctx.advanced);
            case 'surjection':
              // Surjection, not injective: |A| = 4 > |B| = 3. Inj ✗ Surj ✓ Bij ✗
              return presetSurjection(ctx.advanced);
            default:
              return _s;
          }
        },
      };
    },

    applyShotState(_state): State {
      // Shot: non-injective map — two inputs map to the same output (collision visible)
      // 'a' → '1', 'b' → '1', 'c' → '2' — two arrows into '1', '3' is unhit
      return {
        domain: DEFAULT_DOMAIN.slice(),
        codomain: DEFAULT_CODOMAIN.slice(),
        map: [
          ['a', '1'],
          ['b', '1'],
          ['c', '2'],
        ],
        advanced: true,
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
